import { useState } from 'react';
import { FileText, Download, Eye, CheckCircle, XCircle, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import ConfirmModal from './ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DocumentoItem({ documento, tipoDoc, nombreDoc, expedienteId, userRole, validacion, onUpdate, tipoFormulario, documentoIndex }) {
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [validando, setValidando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);

  const getSignedUrl = async (url) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/archivos/url-firmada?url=${encodeURIComponent(url)}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        return data.signed_url;
      }
      
      return url;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return url;
    }
  };

  const handleVerDocumento = async () => {
    setLoadingUrl(true);
    // FIX: Usar URL directa sin firmar (las URLs ya son públicas)
    setSignedUrl(documento.url);
    setShowImageModal(true);
    setLoadingUrl(false);
  };

  const handleValidar = async (aprobado) => {
    setValidando(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteId}/validar-documento`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo_documento: tipoDoc,
            aprobado: aprobado,
            motivo_rechazo: aprobado ? null : motivo
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowRejectModal(false);
        setMotivo('');
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al validar documento');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setValidando(false);
    }
  };

  const handleRechazar = () => {
    if (!motivo.trim()) {
      toast.error('Debes proporcionar un motivo de rechazo');
      return;
    }
    handleValidar(false);
  };

  const handleEliminarDocumento = async () => {
    setEliminando(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteId}/documentos/${tipoFormulario}/${documentoIndex}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Documento eliminado: ${nombreDoc}`);
        setShowDeleteModal(false);
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar documento');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setEliminando(false);
    }
  };

  const getEstadoDocumento = () => {
    if (!documento) return { label: 'Pendiente', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '⏳' };
    if (validacion?.aprobado) return { label: 'Aprobado', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '✓' };
    if (validacion && !validacion.aprobado) return { label: 'Rechazado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '✗' };
    return { label: 'Cargado', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: '📄' };
  };

  const estado = getEstadoDocumento();

  if (!documento) {
    return (
      <div className="flex items-center justify-between p-3 bg-gray-900/30 rounded-lg border border-gray-700">
        <div className="flex items-center gap-3 flex-1">
          <FileText className="w-5 h-5 text-gray-500" />
          <div className="flex-1">
            <p className="text-gray-300 font-medium">{nombreDoc}</p>
            <p className="text-xs text-gray-500">No cargado</p>
          </div>
          <Badge className={estado.color}>
            {estado.icon} {estado.label}
          </Badge>
        </div>
      </div>
    );
  }

  const esImagen = documento.url?.match(/\.(png|jpg|jpeg|webp)$/i);
  const esPDF = documento.url?.match(/\.pdf$/i);

  return (
    <>
      <div className="flex items-start justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700 gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-5 h-5 text-yellow-500" />
            <div className="flex-1">
              <p className="text-gray-300 font-medium">{nombreDoc}</p>
              <p className="text-xs text-gray-500">{documento.nombre}</p>
            </div>
            {validacion && (
              <Badge className={validacion.aprobado ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                {validacion.aprobado ? '✓ Aprobado' : '✗ Rechazado'}
              </Badge>
            )}
          </div>
          
          {validacion && !validacion.aprobado && (
            <p className="text-xs text-red-400 mt-1">
              Motivo: {validacion.motivo_rechazo}
            </p>
          )}
          
          <div className="flex gap-2 mt-2 flex-wrap">
            {/* Botón Ver */}
            {(esImagen || esPDF) && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleVerDocumento}
                disabled={loadingUrl}
                className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
              >
                {loadingUrl ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-1" />
                )}
                Ver
              </Button>
            )}
            
            {/* Botón Descargar */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // FIX: Usar URL directa sin firmar
                window.open(documento.url, '_blank');
              }}
              className="text-gray-400 border-gray-600 hover:bg-gray-700"
            >
              <Download className="w-4 h-4 mr-1" />
              Descargar
            </Button>
            
            {/* Botones de Validación */}
            {(userRole === 'admin' || userRole === 'gerencial') && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleValidar(true)}
                  disabled={validando || validacion?.aprobado}
                  style={{ display: validacion?.aprobado ? 'none' : undefined }}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
                >
                  <Loader2 className={`w-4 h-4 mr-1 ${validando ? 'animate-spin' : 'hidden'}`} />
                  <CheckCircle className={`w-4 h-4 mr-1 ${validando ? 'hidden' : ''}`} />
                  <span>{validando ? 'Validando...' : 'Aprobar'}</span>
                </Button>
                
                <Button
                  size="sm"
                  onClick={() => setShowRejectModal(true)}
                  disabled={validando}
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  {validacion?.aprobado ? 'Revertir' : 'Rechazar'}
                </Button>
                
                {/* NUEVO: Botón Eliminar Documento */}
                <Button
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                  className="bg-red-600/20 hover:bg-red-600/30 text-red-500 border border-red-600/30"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Eliminar
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal para visualizar documento (MEJORADO: soporta PDFs e imágenes) */}
      {showImageModal && (esImagen || esPDF) && signedUrl && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowImageModal(false);
            setSignedUrl(null);
          }}
        >
          <div 
            className="max-w-6xl max-h-[95vh] w-full bg-gray-900 rounded-lg p-4 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold text-lg">{nombreDoc}</h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowImageModal(false);
                  setSignedUrl(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕ Cerrar
              </Button>
            </div>
            
            {esImagen ? (
              <img 
                src={signedUrl} 
                alt={nombreDoc}
                className="w-full h-auto rounded"
                onError={(e) => {
                  console.error('Error loading image:', e);
                  toast.error('Error al cargar imagen');
                }}
              />
            ) : esPDF ? (
              <iframe
                src={signedUrl}
                className="w-full h-[85vh] rounded border border-gray-700 bg-white"
                title={nombreDoc}
                onError={() => toast.error('Error al cargar PDF')}
              />
            ) : (
              <p className="text-gray-400 text-center p-8">No se puede previsualizar este tipo de archivo</p>
            )}
          </div>
        </div>
      )}

      {/* Modal de rechazo */}
      <ConfirmModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setMotivo('');
        }}
        onConfirm={handleRechazar}
        title={validacion?.aprobado ? `Revertir Aprobación de ${nombreDoc}` : `Rechazar ${nombreDoc}`}
        message={
          <div className="space-y-4">
            {validacion?.aprobado && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-3">
                <p className="text-orange-300 text-sm">
                  ⚠️ Este documento fue aprobado anteriormente. Al revertir, el documento volverá a estado rechazado.
                </p>
              </div>
            )}
            
            <p className="text-gray-300">
              {validacion?.aprobado 
                ? '¿Estás seguro de que deseas revertir la aprobación de este documento?'
                : '¿Estás seguro de que deseas rechazar este documento?'}
            </p>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-300 text-sm">
                ⚠️ Usa el Centro de Comunicaciones para enviar solicitudes personalizadas al cliente
              </p>
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Motivo del rechazo: *
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: Imagen borrosa, documento incompleto, no se leen los datos..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
                rows="3"
                required
              />
            </div>
          </div>
        }
        confirmText={validando ? "Procesando..." : (validacion?.aprobado ? "Sí, Revertir" : "Sí, Rechazar")}
        cancelText="Cancelar"
        isDangerous={true}
      />
      
      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleEliminarDocumento}
        title={`Eliminar ${nombreDoc}`}
        message={
          <div className="space-y-4">
            <p className="text-gray-300">
              ¿Estás seguro de que deseas eliminar este documento?
            </p>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-300 text-sm font-semibold">
                ⚠️ Esta acción es PERMANENTE
              </p>
              <p className="text-red-300 text-xs mt-1">
                El archivo se eliminará de Cloudinary y de la base de datos. No se puede deshacer.
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <p className="text-white text-sm"><strong>Documento:</strong> {nombreDoc}</p>
              <p className="text-white text-sm"><strong>Archivo:</strong> {documento.nombre}</p>
            </div>
          </div>
        }
        confirmText={eliminando ? "Eliminando..." : "Sí, Eliminar"}
        cancelText="Cancelar"
        isDangerous={true}
        loading={eliminando}
      />
    </>
  );
}

export default function DocumentosSection({ expediente, expedienteId, userRole, onUpdate }) {
  const form1Docs = expediente?.formulario_1_documentos || [];
  const form2Docs = expediente?.formulario_2_documentos || [];

  const cedulaDoc = form1Docs.find(d => {
    const name = d.nombre?.toLowerCase() || '';
    return name.includes('cedula') || name.includes('cédula');
  });
  const cedulaIndex = form1Docs.findIndex(d => {
    const name = d.nombre?.toLowerCase() || '';
    return name.includes('cedula') || name.includes('cédula');
  });
  
  const rifDoc = form1Docs.find(d => d.nombre?.toLowerCase().includes('rif'));
  const rifIndex = form1Docs.findIndex(d => d.nombre?.toLowerCase().includes('rif'));
  
  const certBancariaDoc = form2Docs.find(d => d.nombre?.toLowerCase().includes('certificacion') || d.nombre?.toLowerCase().includes('bancaria'));
  const certBancariaIndex = form2Docs.findIndex(d => d.nombre?.toLowerCase().includes('certificacion') || d.nombre?.toLowerCase().includes('bancaria'));
  
  const declaracionDoc = expediente?.firma_url ? {
    nombre: "Declaración Jurada Firmada",
    url: expediente.firma_url
  } : null;

  const validaciones = expediente?.documentos_validados || {};

  return (
    <Card className="bg-gray-800/50 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-yellow-500" />
            Documentos
          </div>
          {(userRole === 'admin' || userRole === 'gerencial') && (
            <Badge variant="outline" className="text-gray-400 border-gray-600">
              Validar documentos
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Cédula de Identidad */}
        <DocumentoItem
          documento={cedulaDoc}
          tipoDoc="cedula"
          nombreDoc="Cédula de Identidad"
          expedienteId={expedienteId}
          userRole={userRole}
          validacion={validaciones.cedula}
          onUpdate={onUpdate}
          tipoFormulario="formulario_1"
          documentoIndex={cedulaIndex}
        />

        {/* RIF */}
        <DocumentoItem
          documento={rifDoc}
          tipoDoc="rif"
          nombreDoc="RIF"
          expedienteId={expedienteId}
          userRole={userRole}
          validacion={validaciones.rif}
          onUpdate={onUpdate}
          tipoFormulario="formulario_1"
          documentoIndex={rifIndex}
        />

        {/* Certificación Bancaria */}
        {certBancariaDoc && (
          <DocumentoItem
            documento={certBancariaDoc}
            tipoDoc="certificacion_bancaria"
            nombreDoc="Certificación Bancaria"
            expedienteId={expedienteId}
            userRole={userRole}
            validacion={validaciones.certificacion_bancaria}
            onUpdate={onUpdate}
            tipoFormulario="formulario_2"
            documentoIndex={certBancariaIndex}
          />
        )}

        {/* Recibos de Pago */}
        {form2Docs.filter(d => d.nombre?.toLowerCase().includes('recibo')).map((recibo, index) => {
          const reciboIndex = form2Docs.findIndex(d => d === recibo);
          return (
            <DocumentoItem
              key={recibo.cloudinary_id || index}
              documento={recibo}
              tipoDoc={`recibo_${index + 1}`}
              nombreDoc={recibo.nombre || `Recibo de Pago #${index + 1}`}
              expedienteId={expedienteId}
              userRole={userRole}
              validacion={validaciones[`recibo_${index + 1}`]}
              onUpdate={onUpdate}
              tipoFormulario="formulario_2"
              documentoIndex={reciboIndex}
            />
          );
        })}

        {/* Documentos Adicionales (que no son Certificación Bancaria ni Recibos) */}
        {form2Docs.filter(d => 
          !d.nombre?.toLowerCase().includes('certificacion') && 
          !d.nombre?.toLowerCase().includes('bancaria') && 
          !d.nombre?.toLowerCase().includes('recibo')
        ).map((docAdicional, index) => {
          const docIndex = form2Docs.findIndex(d => d === docAdicional);
          return (
            <DocumentoItem
              key={docAdicional.cloudinary_id || `adicional_${index}`}
              documento={docAdicional}
              tipoDoc={`documento_adicional_${index + 1}`}
              nombreDoc={docAdicional.nombre || `Documento Adicional #${index + 1}`}
              expedienteId={expedienteId}
              userRole={userRole}
              validacion={validaciones[`documento_adicional_${index + 1}`]}
              onUpdate={onUpdate}
              tipoFormulario="formulario_2"
              documentoIndex={docIndex}
            />
          );
        })}

        {/* Declaración Jurada Firmada */}
        {declaracionDoc && (
          <>
            <div className="border-t border-gray-700 my-4 pt-3">
              <p className="text-gray-400 text-sm font-medium mb-3">Declaración Jurada</p>
            </div>
            <DocumentoItem
              documento={declaracionDoc}
              tipoDoc="declaracion_jurada"
              nombreDoc="Declaración Jurada de Retiro Formal"
              expedienteId={expedienteId}
              userRole={userRole}
              validacion={validaciones.declaracion_jurada}
              onUpdate={onUpdate}
              tipoFormulario="otros"
              documentoIndex={-1}
            />
          </>
        )}

        {/* Info para gerenciales */}
        {(userRole === 'admin' || userRole === 'gerencial') && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mt-4">
            <p className="text-blue-300 text-sm">
              <strong>💡 Instrucciones:</strong>
            </p>
            <ul className="text-blue-300 text-xs mt-2 space-y-1 list-disc list-inside">
              <li>Click &quot;Ver&quot; para revisar el documento</li>
              <li>Verifica que esté claro, legible y completo</li>
              <li>Click &quot;Aprobar&quot; si está correcto</li>
              <li>Click &quot;Rechazar&quot; si necesita corrección</li>
              <li>Usa &quot;Centro de Comunicaciones&quot; para solicitudes personalizadas</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
